<template>
  <v-card flat>
    <v-card-text>
      <div v-if="isLoading" class="d-flex justify-center align-center py-10">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
        <span class="ml-3">Cargando usuarios...</span>
      </div>

      <div v-else>
        <p>Total de usuarios {{ contactsWithSubrole.length }}</p>
        <v-data-table
          :headers="headers"
          :items="contactsWithSubrole"
          class="elevation-1"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:item.user="{ item }">
            <div class="d-flex align-center py-2">
              <v-avatar size="40" class="mr-3">
                <v-img
                  :src="getProfileImg(item.media)"
                  :alt="item.name"
                ></v-img>
              </v-avatar>
              <div>
                <div class="font-weight-bold">{{ item.name || "--" }}</div>
                <div class="text-caption grey--text">{{ item.email }}</div>
                <div class="text-caption grey--text">
                  {{ item.phone || "--" }}
                </div>
              </div>
            </div>
          </template>

          <template v-slot:item.role="{ item }">
            <div class="d-flex justify-center py-2">
              <v-chip
                v-if="item.role"
                small
                :color="getRoleChipColor(item.role)"
                text-color="white"
                class="font-weight-bold"
              >
                {{ formatUserRoleLabel(item.role) }}
              </v-chip>
            </div>
          </template>

          <template v-slot:item.subrole="{ item }">
            <div class="d-flex justify-end align-center">
              <v-select
                v-model="item.currentSubrole"
                :items="availableSubroleItems"
                item-text="name"
                item-value="id"
                dense
                outlined
                clearable
                hide-details
                style="max-width: 220px"
                class="subrole-select"
                @change="onSubroleChange(item, $event)"
                :disabled="savingUserId === item.user_id || !item.user_id"
                :loading="savingUserId === item.user_id"
              >
                <template v-slot:selection="{ item: subroleItem }">
                  <v-chip
                    v-if="subroleItem"
                    small
                    color="primary"
                    text-color="white"
                    class="font-weight-bold ma-0"
                  >
                    {{ subroleItem.name }}
                  </v-chip>
                </template>
              </v-select>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import { GetSubroles, AssignSubrole } from "@/api/subrole-api";

export default {
  name: "UserPermissionsList",
  data() {
    return {
      isLoading: true,
      contactsList: [],
      customSubroles: [],
      savingUserId: null,
      headers: [
        { text: "Usuario", value: "user", align: "start", sortable: true },
        {
          text: "Rol",
          value: "role",
          align: "center",
          sortable: true,
          width: "12px"
        },
        {
          text: "Perfil de acceso asignado",
          value: "subrole",
          align: "end",
          sortable: false,
          width: "244px"
        }
      ]
    };
  },
  computed: {
    availableSubroleItems() {
      return this.customSubroles;
    },
    contactsWithSubrole() {
      return this.contactsList.map(contact => ({
        ...contact,
        currentSubrole: this.contactAssignedSubroleId(contact)
      }));
    }
  },
  async mounted() {
    await this.getContactsList();
    await this.fetchCustomSubroles();
  },
  methods: {
    ...mapActions("contacts", ["fetchContactList"]),

    /** Solo ID de subrol personalizado; sin subrol el select queda vacío. */
    contactAssignedSubroleId(contact) {
      if (contact.subrole_id == null || contact.subrole_id === "") return null;
      return String(contact.subrole_id);
    },

    formatUserRoleLabel(role) {
      if (!role) return "";
      const key = String(role).toLowerCase();
      const labels = {
        admin: "Admin",
        broker: "Broker",
        root: "Root",
        developer: "Developer",
        marketing: "Marketing",
        coordinator: "Coordinador"
      };
      return labels[key] || this.capitalizeRole(role);
    },

    capitalizeRole(role) {
      const s = String(role);
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },

    getRoleChipColor(role) {
      if (!role) return "grey";
      const key = String(role).toLowerCase();
      if (key === "admin" || key === "root") return "red darken-1";
      if (key === "broker") return "blue darken-1";
      if (key === "developer") return "deep-purple";
      if (key === "marketing") return "teal";
      if (key === "coordinator") return "orange darken-2";
      return "grey darken-1";
    },

    filterContactsByStatus(list) {
      return list
        .filter(item => {
          return item.status !== 0 || item.performance.totalLeads >= 1;
        })
        .sort((a, b) => a.status - b.status);
    },

    async fetchCustomSubroles() {
      const res = await GetSubroles();
      this.customSubroles = res.subroles.map(r => ({
        ...r,
        id: r._id,
        name: r.name,
        isSystem: false
      }));
    },

    async getContactsList() {
      this.isLoading = true;
      await this.fetchContactList()
        .then(res => {
          this.contactsList = this.filterContactsByStatus(res || []);
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },

    async onSubroleChange(item, newSubroleValue) {
      if (!item.user_id) {
        this.showSnackbar(
          "El contacto no tiene un usuario válido para asignar subrol.",
          "error"
        );
        await this.getContactsList();
        return;
      }

      this.savingUserId = item.user_id;

      try {
        const cleared =
          newSubroleValue === null ||
          newSubroleValue === undefined ||
          newSubroleValue === "";

        if (cleared) {
          await AssignSubrole(item.user_id, null);
          this.showSnackbar(
            "Subrol quitado; el usuario usa solo los permisos de su rol.",
            "success"
          );
        } else {
          await AssignSubrole(item.user_id, newSubroleValue);
          this.showSnackbar("Subrol asignado correctamente.", "success");
        }
        await this.getContactsList();
      } catch (err) {
        this.showSnackbar(
          "Error al actualizar el subrol del usuario.",
          "error"
        );
        await this.getContactsList();
      } finally {
        this.savingUserId = null;
      }
    },

    showSnackbar(text, color) {
      if (color === "success") {
        this.$snotify.success(text, "Completado");
      } else if (color === "error") {
        this.$snotify.error(text, "Error");
      } else {
        this.$snotify.info(text, "Información");
      }
    },

    getProfileImg(media) {
      if (media && media.hasOwnProperty("featured_image")) {
        if (
          media.featured_image.hasOwnProperty("src") &&
          media.featured_image.src
        ) {
          return media.featured_image.src;
        } else if (
          media.featured_image.hasOwnProperty("src_avatar") &&
          media.featured_image.src_avatar
        ) {
          return media.featured_image.src_avatar;
        }
      }
      return "https://upload.wikimedia.org/wikipedia/commons/thumb/5/57/OOjs_UI_icon_userAvatar-progressive.svg/600px-OOjs_UI_icon_userAvatar-progressive.svg.png";
    }
  }
};
</script>
